class AtlDeliverySignature extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.cart = JSON.parse(this.querySelector('[data-signature-cart]').textContent);
    this.accepted = false;
    this.pending = new Set();
    document.addEventListener('change', (event) => {
      if (!event.target.matches('[data-signature-checkbox]')) return;
      this.accepted = event.target.checked;
      this.message = '';
      this.render();
    }, options);
    for (const name of ['shopify:cart:lines-update', 'shopify:cart:discount-update']) {
      document.addEventListener(name, (event) => {
        const update = Promise.resolve(event.promise).catch(() => {}).then(() => this.refresh());
        this.pending.add(update);
        update.finally(() => this.pending.delete(update));
      }, options);
    }
    document.addEventListener('click', (event) => {
      const target = event.target.closest('button[name="checkout"], a[href]');
      if (!target) return;
      const isCheckout = target.matches('button[name="checkout"]') || new URL(target.href, location.href).pathname.endsWith('/checkout');
      if (!isCheckout) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.checkout(target);
    }, { ...options, capture: true });
    document.addEventListener('submit', (event) => {
      if (!event.target.matches('form[action$="/cart"]') || event.submitter?.name === 'update') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.checkout(event.submitter);
    }, { ...options, capture: true });
    window.addEventListener('pageshow', () => this.refresh(), options);
    this.observer = new MutationObserver(() => this.render());
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.render();
    this.refresh();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  fingerprint(cart) {
    return JSON.stringify([cart.currency, cart.total_price, cart.items.map(item => [item.key, item.quantity, item.final_line_price])]);
  }

  required(cart = this.cart) {
    return cart.requires_shipping && (cart.currency !== 'USD' || this.dataset.taxesIncluded === 'true' || cart.total_price > 49900);
  }

  async request(path, options = {}) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart${path}.js`, { cache: 'no-store', ...options });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  async refresh() {
    try {
      const cart = await this.request('');
      if (this.fingerprint(cart) !== this.fingerprint(this.cart)) this.accepted = false;
      this.cart = cart;
      this.render();
      return true;
    } catch {
      this.message = this.dataset.error;
      this.render();
      return false;
    }
  }

  render() {
    document.querySelectorAll('#cart-drawer .cart__ctas, .cart-page .cart__ctas').forEach(ctas => {
      if (!ctas.previousElementSibling?.matches('[data-atl-signature]')) {
        ctas.before(this.querySelector('template').content.cloneNode(true));
      }
    });
    document.querySelectorAll('[data-atl-signature]').forEach(panel => {
      panel.hidden = !this.required() && !this.message;
      const checkbox = panel.querySelector('input');
      checkbox.checked = this.accepted;
      checkbox.required = this.required();
      const status = panel.querySelector('[data-signature-status]');
      status.hidden = !this.message;
      if (status.textContent !== (this.message || '')) status.textContent = this.message || '';
    });
  }

  async checkout(button) {
    if (this.busy) return;
    this.busy = true;
    button?.setAttribute('aria-busy', 'true');
    try {
      await Promise.all([...this.pending]);
      if (!await this.refresh()) return;
      if (this.required() && !this.accepted) {
        this.message = this.dataset.required;
        this.render();
        const scope = button?.closest('.cart-drawer__summary, .cart-page__summary') || document;
        [...scope.querySelectorAll('[data-signature-checkbox]')].find(input => input.getClientRects().length)?.focus();
        return;
      }
      const before = this.fingerprint(this.cart);
      const acknowledged = this.required() && this.accepted;
      const cart = await this.request('/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: {
          'Delivery signature acknowledgment': acknowledged ? this.querySelector('template').content.querySelector('input').value : '',
          'Delivery signature acknowledged at': acknowledged ? new Date().toISOString() : '',
          'Delivery signature policy': acknowledged ? 'USD >499; after discounts; excludes shipping and tax; v1' : ''
        } })
      });
      this.cart = cart;
      if (before !== this.fingerprint(cart)) {
        this.accepted = false;
        this.message = this.dataset.required;
        this.render();
        return;
      }
      location.assign(`${window.Shopify?.routes?.root || '/'}checkout`);
    } catch {
      this.message = this.dataset.error;
      this.render();
    } finally {
      this.busy = false;
      button?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('atl-delivery-signature')) customElements.define('atl-delivery-signature', AtlDeliverySignature);
